import { ThemeEvents } from '@theme/events';

class WegigaMobilePurchase extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.action = this.querySelector('[data-wegiga-mobile-action]');
    if (!this.section || !this.action || !this.findForm()) return;

    this.abortController = new AbortController();
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.spacer = document.createElement('div');
    this.spacer.className = 'wegiga-mobile-purchase-spacer';
    this.spacer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.spacer);
    this.section.classList.add('wegiga-mobile-purchase-enabled');

    const { signal } = this.abortController;
    this.action.addEventListener('click', this.openOptions, { signal });
    this.section.addEventListener(ThemeEvents.variantUpdate, this.updateVariant, { signal });
    this.section.addEventListener(ThemeEvents.variantSelected, this.queueSync, { signal });
    window.addEventListener('scroll', this.queueSync, { passive: true, signal });
    window.addEventListener('resize', this.queueSync, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', this.queueSync, { passive: true, signal });
    document.addEventListener('focusin', this.queueSync, { signal });
    document.addEventListener('focusout', this.queueSync, { signal });
    document.addEventListener('toggle', this.queueSync, { capture: true, signal });

    this.observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => !this.contains(mutation.target) && mutation.target !== this.spacer)) {
        this.queueSync();
      }
    });
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'aria-hidden', 'hidden', 'aria-modal', 'disabled'],
    });
    this.queueSync();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    this.spacer?.remove();
    this.section?.classList.remove('wegiga-mobile-purchase-enabled');
    window.cancelAnimationFrame(this.frame);
    window.clearTimeout(this.focusTimer);
    this.highlightedTarget?.classList.remove('wegiga-mobile-purchase-target');
  }

  findForm() {
    return this.section?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  findPicker() {
    return this.section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
  }

  queueSync = () => {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0;
      this.syncVisibility();
    });
  };

  isVisible(element) {
    if (!element || !element.getClientRects().length) return false;
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    return rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < window.innerWidth;
  }

  syncVisibility() {
    if (!this.isConnected || !this.spacer) return;
    const button = this.findForm()?.querySelector('[ref="addToCartButton"], button[type="submit"][name="add"]');
    const activeElement = document.activeElement;
    const isTyping = activeElement?.matches(
      'textarea, select, [contenteditable="true"], input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="range"])'
    );
    const keyboardOpen = window.visualViewport && window.innerHeight - window.visualViewport.height > 150;
    const blockingOverlay = [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(
      (element) => element.getAttribute('aria-hidden') !== 'true' && this.isVisible(element)
    );
    const show = this.mobile.matches && Boolean(button) && !this.isVisible(button) && !isTyping && !keyboardOpen && !blockingOverlay;
    this.hidden = !show;
    this.spacer.dataset.active = String(show);
    if (show) this.spacer.style.setProperty('--wegiga-mobile-purchase-height', `${this.offsetHeight}px`);
  }

  updateVariant = (event) => {
    const detail = event.detail;
    if (!detail?.data) return;
    if (!detail.data.newProduct && String(detail.data.productId) !== this.dataset.productId) return;
    const updated = detail.data.html?.querySelector(
      `wegiga-mobile-purchase[data-section-id="${this.dataset.sectionId}"]`
    );
    if (updated) {
      const currentDetails = this.querySelector('[data-wegiga-mobile-details]');
      const newDetails = updated.querySelector('[data-wegiga-mobile-details]');
      if (currentDetails && newDetails) currentDetails.replaceChildren(...newDetails.cloneNode(true).childNodes);
      this.dataset.productId = updated.dataset.productId;
      this.dataset.sizeOptionIndex = updated.dataset.sizeOptionIndex;
      const label = updated.querySelector('[data-wegiga-mobile-action]')?.textContent;
      if (label) this.action.textContent = label;
    }
    this.queueSync();
  };

  openOptions = () => {
    const picker = this.findPicker();
    const form = this.findForm();
    const sizeIndex = Number(this.dataset.sizeOptionIndex);
    const sizeGroup = sizeIndex >= 0 ? (
      picker?.querySelector(`fieldset[data-fieldset-index="${sizeIndex}"]`) ||
      picker?.querySelector(`option[data-input-id^="${sizeIndex + 1}-"]`)?.closest('.variant-option')
    ) : null;
    const pickerGroup = sizeGroup || picker?.querySelector('fieldset, .variant-option:has(select)');
    const purchaseButton = form?.querySelector('[ref="addToCartButton"], button[type="submit"][name="add"]');
    const target = pickerGroup || picker || form;
    if (!target) return;

    const focusTarget = target.querySelector('input[type="radio"]:checked:not(:disabled), select:not(:disabled)') ||
      target.querySelector('input[type="radio"]:not(:disabled), input:not([type="hidden"]):not(:disabled)') || purchaseButton;
    this.highlightedTarget?.classList.remove('wegiga-mobile-purchase-target');
    window.clearTimeout(this.focusTimer);
    target.classList.add('wegiga-mobile-purchase-target');
    this.highlightedTarget = target;
    target.scrollIntoView({ behavior: this.reducedMotion.matches ? 'instant' : 'smooth', block: 'center' });
    focusTarget?.focus({ preventScroll: true });
    this.focusTimer = window.setTimeout(() => {
      target.classList.remove('wegiga-mobile-purchase-target');
      this.highlightedTarget = null;
    }, 2400);
    this.queueSync();
  };
}

if (!customElements.get('wegiga-mobile-purchase')) {
  customElements.define('wegiga-mobile-purchase', WegigaMobilePurchase);
}
